import React from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  Modal,
  Fade,
  Backdrop,
  IconButton,
} from "@mui/material";
import { Close, ShoppingCartOutlined, OpenInNew } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const QuickViewModal = ({ open, handleClose, product, brand }) => {
  const navigate = useNavigate();

  if (!product) return null;

  const isLowStock =
    product.quantity_in_stock > 0 && product.quantity_in_stock <= 5;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", md: 850 },
            bgcolor: "background.paper",
            borderRadius: "20px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
            p: { xs: 2, md: 5 },
            outline: "none",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: brand.muted,
            }}
          >
            <Close />
          </IconButton>

          {/* Left: Image Section */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#f9f9f9",
              borderRadius: "12px",
              p: 2,
            }}
          >
            <img
              src={product.photo || "https://via.placeholder.com/400"}
              alt={product.name}
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Right: Info Section */}
          <Box sx={{ flex: 1.2, display: "flex", flexDirection: "column" }}>
            <Typography
              variant="overline"
              sx={{ color: brand.muted, fontWeight: 700 }}
            >
              SKU:{" "}
              {product.sku ||
                product.id.toString().substring(0, 8).toUpperCase()}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: brand.navy,
                mb: 1,
                fontFamily: "Poppins",
              }}
            >
              {product.name}
            </Typography>

            {isLowStock && (
              <Typography
                variant="body2"
                sx={{ color: brand.danger, fontWeight: 800, mb: 2 }}
              >
                ⚠️ Hurry! Only {product.quantity_in_stock} left in stock.
              </Typography>
            )}

            <Typography
              variant="body1"
              sx={{ color: brand.textLight, mb: 3, lineHeight: 1.6 }}
            >
              {product.description ||
                "Premium enterprise-grade inventory item. Detailed specifications available in the full product report."}
            </Typography>

            <Box sx={{ mt: "auto" }}>
              <Divider sx={{ mb: 3 }} />
              <Typography
                variant="h3"
                sx={{ color: brand.navy, fontWeight: 900, mb: 3 }}
              >
                {product.selling_price}{" "}
                <small style={{ fontSize: "1.2rem", color: brand.textLight }}>
                  {product.currency || "EGP"}
                </small>
              </Typography>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ShoppingCartOutlined />}
                  disabled={product.quantity_in_stock <= 0}
                  sx={{
                    bgcolor: brand.navy,
                    py: 1.5,
                    borderRadius: "8px",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": { bgcolor: "#1C2A3D" },
                  }}
                >
                  Add to Cart
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  onClick={() => {
                    handleClose();
                    navigate(`/shopProductPage/${product.id}`);
                  }}
                  sx={{
                    borderColor: brand.navy,
                    color: brand.navy,
                    borderRadius: "8px",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#1C2A3D",
                      bgcolor: "rgba(46,64,83,0.05)",
                    },
                  }}
                >
                  Full Details
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default QuickViewModal;
